import { useRef, useState, useCallback, useEffect } from "react";
import { ArrowLeft, ArrowRight, Play, X } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

/**
 * VideoShowcase — Caraway-style horizontal video carousel.
 * - Vertical (9:16) thumbnail cards with a play-button overlay
 * - Native horizontal scroll + snap (works with touch swipe AND mouse drag)
 * - Prev/Next arrows for desktop
 * - Clicking a card opens a YouTube modal (autoplay)
 */
export default function VideoShowcase({ kicker, title, intro, videos = [] }) {
  const scrollerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  const scrollByCard = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-video-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  // Mouse-drag-to-swipe support on desktop (touch swipe is native)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    const onDown = (e) => {
      isDown = true;
      startX = e.pageX;
      startScroll = el.scrollLeft;
      el.classList.add("cursor-grabbing");
    };
    const onMove = (e) => {
      if (!isDown) return;
      el.scrollLeft = startScroll - (e.pageX - startX);
    };
    const onUp = () => {
      isDown = false;
      el.classList.remove("cursor-grabbing");
    };
    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  if (!videos.length) return null;

  return (
    <section className="bg-[var(--abs-cream)] py-24 md:py-32 overflow-hidden" data-testid="video-showcase">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left column — heading */}
          <div className="col-span-12 lg:col-span-4 lg:sticky lg:top-32">
            <ScrollReveal>
              {kicker && (
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-6">
                  {kicker}
                </div>
              )}
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
                {title}
              </h2>
              {intro && (
                <p className="mt-8 text-[16px] leading-relaxed text-[var(--abs-mute)] max-w-md">
                  {intro}
                </p>
              )}

              {/* Desktop arrows */}
              <div className="hidden lg:flex gap-3 mt-10" data-testid="video-arrows">
                <button
                  type="button"
                  onClick={() => scrollByCard(-1)}
                  disabled={!canScrollLeft}
                  data-testid="video-prev"
                  aria-label="Previous"
                  className="h-12 w-12 grid place-items-center border border-[var(--abs-ink)] text-[var(--abs-ink)] hover:bg-[var(--abs-ink)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollByCard(1)}
                  disabled={!canScrollRight}
                  data-testid="video-next"
                  aria-label="Next"
                  className="h-12 w-12 grid place-items-center border border-[var(--abs-ink)] text-[var(--abs-ink)] hover:bg-[var(--abs-ink)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </ScrollReveal>
          </div>

          {/* Right column — video carousel */}
          <div className="col-span-12 lg:col-span-8 relative -mx-6 lg:mx-0">
            <div
              ref={scrollerRef}
              className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-2 px-6 lg:px-0 cursor-grab select-none scrollbar-hide"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
              data-testid="video-scroller"
            >
              {videos.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-video-card
                  data-testid={`video-card-${v.id}`}
                  onClick={() => setActiveId(v.id)}
                  className="relative shrink-0 snap-start group block w-[85vw] sm:w-[460px] md:w-[440px] lg:w-[480px] xl:w-[520px] overflow-hidden bg-[var(--abs-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--abs-orange)]"
                >
                  <div className="relative aspect-video w-full overflow-hidden">
                    <img
                      src={v.thumb}
                      alt={v.caption}
                      draggable={false}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/55" />

                    {/* Play button — white outline, Caraway-style */}
                    <div className="absolute inset-0 grid place-items-center">
                      <div className="h-16 w-16 md:h-[72px] md:w-[72px] rounded-full border-2 border-white grid place-items-center backdrop-blur-[1px] bg-white/10 transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-6 w-6 text-white fill-white translate-x-0.5" />
                      </div>
                    </div>

                    {/* Caption badge — bottom-left */}
                    {v.caption && (
                      <div className="absolute left-3 bottom-3">
                        <span className="inline-block bg-[var(--abs-cream)] text-[var(--abs-ink)] text-[12px] md:text-[13px] px-3 py-1.5 font-medium tracking-tight">
                          {v.caption}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Below-card CTA label */}
                  {v.ctaLabel && (
                    <div className="bg-[var(--abs-cream)] text-center pt-5 pb-1">
                      <span className="serif text-[15px] md:text-[16px] text-[var(--abs-ink)] group-hover:text-[var(--abs-orange)] transition-colors inline-flex items-center gap-2">
                        {v.ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Player modal */}
      {activeId && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 grid place-items-center px-4 animate-[fadeDown_.2s_ease-out]"
          onClick={() => setActiveId(null)}
          data-testid="video-modal"
        >
          <button
            type="button"
            onClick={() => setActiveId(null)}
            data-testid="video-modal-close"
            aria-label="Close video"
            className="absolute top-5 right-5 md:top-7 md:right-7 text-white/90 hover:text-white"
          >
            <X className="h-7 w-7" />
          </button>
          <div
            className="relative w-full max-w-[960px] aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${activeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title="ABS Solutions video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0 bg-black"
            />
          </div>
        </div>
      )}
    </section>
  );
}
